import { AmbiguousError } from "../../types/AmbiguousError";
import TerminalError from "../../types/TerminalError";
import TerminalLine from "../../types/TerminalLine";
import TerminalMessage from "../../types/TerminalMessage";
import { convertAmbiguousErrorToArray } from "../../utils/ambiguousErrors";
import { getStore } from "../store/appStore";
import { AppAction } from "../types";
import { TerminalActionKey } from "./ActionKeys";

/** Action to append one or more lines to the terminal. */
export interface AppendTerminalAction
  extends AppAction<
    TerminalActionKey.APPEND_TERMINAL,
    { lines: TerminalLine[]; forceShow: boolean }
  > {}

/** Action to clear all lines from the terminal. */
export interface ClearTerminalAction
  extends AppAction<TerminalActionKey.CLEAR_TERMINAL> {}

export interface SetTerminalExpandedAction
  extends AppAction<
    TerminalActionKey.SET_TERMINAL_EXPANDED,
    { isExpanded: boolean }
  > {}

/** A terminal action. */
export type TerminalAction =
  | AppendTerminalAction
  | ClearTerminalAction
  | SetTerminalExpandedAction;

/** Creates an action that appends lines to the terminal when dispatched. */
export const appendTerminal = (
  lines: TerminalLine[],
  forceShow = false
): AppendTerminalAction =>
  getStore().dispatch({
    type: TerminalActionKey.APPEND_TERMINAL,
    payload: { lines, forceShow },
  });

/** Prints an error message into the terminal. */
export const appendTerminalError = (
  message: string,
  ambiguousError: AmbiguousError,
  show: boolean = false
) => {
  const errors = convertAmbiguousErrorToArray(ambiguousError);
  appendTerminal([
    new TerminalMessage(message),
    ...errors.map(error => new TerminalError(error)),
  ]);
  if (show) {
    setTerminalExpanded(true);
  }
};

/** Creates an action that clears the terminal upon dispatch. */
export const clearTerminal = (): ClearTerminalAction =>
  getStore().dispatch({
    type: TerminalActionKey.CLEAR_TERMINAL,
    payload: {},
  });

export const setTerminalExpanded = (
  isExpanded: boolean
): SetTerminalExpandedAction =>
  getStore().dispatch({
    type: TerminalActionKey.SET_TERMINAL_EXPANDED,
    payload: { isExpanded },
  });

/** Handles command execution for the terminal. */
export const executeCommand = (command: string) => {
  const state = getStore().getState();

  const [func] = command.split(" ").filter(x => x.length);
  const [executable] = func.split("(");
  switch (executable) {
    case "clear":
      clearTerminal();
      break;
    // DEV features..
    case "getState":
      appendTerminal([new TerminalMessage("getState", state)]);
      break;
    default:
      // TODO(cochamberlain): switch this to execute commands on worker.
      // tslint:disable-next-line:no-eval
      const output = eval(command);
      appendTerminal([new TerminalMessage(command, output)]);
      break;
  }
};
